import { useState } from 'react';
import type { CaseFormData, PartyEntry } from '../../types';
import FileUploadZone from './FileUploadZone';
import PartyTypeSelector from './PartyTypeSelector';
import PartyFields from './PartyFields';
import DisputeCards from './DisputeCards';
import Textarea from '../../../components/forms/textarea';

interface WalkInFormProps {
    data: CaseFormData;
    onUpdate: (u: Partial<CaseFormData>) => void;
    onSubmit: () => void;
    showTerms?: boolean;
}

export default function WalkInForm({ data, onUpdate, onSubmit, showTerms }: WalkInFormProps) {
    const [step, setStep] = useState(1);
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [agreedAssistance, setAgreedAssistance] = useState(false);
    const [errors, setErrors] = useState<Record<string, any>>({});

    const updateApplicant = (i: number, field: keyof PartyEntry, value: string) => {
        const next = [...(data.applicants || [])];
        next[i] = { ...next[i], [field]: value };
        onUpdate({ applicants: next });
        // Clear error when typing
        if (errors.applicants?.[i]?.[field]) {
            const newErrors = { ...errors };
            newErrors.applicants[i][field] = '';
            setErrors(newErrors);
        }
    };

    const updateRespondent = (i: number, field: keyof PartyEntry, value: string) => {
        const next = [...(data.respondents || [])];
        next[i] = { ...next[i], [field]: value };
        onUpdate({ respondents: next });
        // Clear error when typing
        if (errors.respondents?.[i]?.[field]) {
            const newErrors = { ...errors };
            newErrors.respondents[i][field] = '';
            setErrors(newErrors);
        }
    };

    const removeApplicant = (i: number) => {
        const next = [...(data.applicants || [])];
        next.splice(i, 1);
        onUpdate({ applicants: next });
        if (errors.applicants) {
            const nextErrors = [...errors.applicants];
            nextErrors.splice(i, 1);
            setErrors({ ...errors, applicants: nextErrors });
        }
    };

    const removeRespondent = (i: number) => {
        const next = [...(data.respondents || [])];
        next.splice(i, 1);
        onUpdate({ respondents: next });
        if (errors.respondents) {
            const nextErrors = [...errors.respondents];
            nextErrors.splice(i, 1);
            setErrors({ ...errors, respondents: nextErrors });
        }
    };

    const validateStep1 = () => {
        const newErrors: Record<string, any> = {};

        const applicantErrors = (data.applicants || []).map((a) => {
            const e: any = {};
            if (!a.name) e.name = 'Name is required';
            if (a.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) e.email = 'Invalid email';
            return e;
        });
        if (applicantErrors.some((e) => Object.keys(e).length > 0)) {
            newErrors.applicants = applicantErrors;
        }

        const respondentErrors = (data.respondents || []).map((r) => {
            const e: any = {};
            if (!r.name) e.name = 'Name is required';
            if (r.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email)) e.email = 'Invalid email';
            return e;
        });
        if (respondentErrors.some((e) => Object.keys(e).length > 0)) {
            newErrors.respondents = respondentErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors: Record<string, any> = {};
        if (!data.description) newErrors.description = 'Description is required';
        if (showTerms) {
            if (!agreedTerms) newErrors.terms = 'You must agree to the terms';
            if (!agreedAssistance) newErrors.assistance = 'You must agree to request assistance';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep1()) {
            setStep(2);
            setErrors({});
            window.scrollTo(0, 0);
        }
    };

    const handleSubmit = () => {
        if (validateStep2()) onSubmit();
    };

    if (step === 1) {
        return (
            <div className="space-y-10">
                <div className="space-y-5">
                    <div>
                        <h2 className="text-xl font-bold text-secondary">Applicant</h2>
                        <p className="mt-1 text-[13px] text-[#555]">Let's start with your details.</p>
                    </div>
                    <PartyTypeSelector
                        value={data.applicantType || 'Individual'}
                        onChange={(v) => onUpdate({ applicantType: v })}
                        label="Who is submitting this dispute?"
                        description="Choose Individual if this case is about you personally.  Choose Company if you're filing on behalf of a business, NGO, or organisation."
                    />
                    <PartyFields
                        entries={data.applicants || []}
                        errors={errors.applicants}
                        onUpdate={updateApplicant}
                        onRemove={removeApplicant}
                        onAdd={() =>
                            onUpdate({
                                applicants: [
                                    ...(data.applicants || []),
                                    { name: '', address: '', phone: '', email: '' },
                                ],
                            })
                        }
                        nameLabel="Name"
                        addLabel="Add new applicant"
                    />
                </div>

                <div className="space-y-5">
                    <div>
                        <h2 className="text-xl font-bold text-secondary">Other Party Details (Respondent)</h2>
                        <p className="mt-1 text-[13px] text-[#555]">
                            Now tell us about the other person or organisation involved.
                        </p>
                    </div>
                    <PartyTypeSelector
                        value={data.respondentType || 'Individual'}
                        onChange={(v) => onUpdate({ respondentType: v })}
                        label="Who is this dispute with?"
                        description="Choose Individual if this dispute is with a person.  Choose Company if the dispute is with a business or organization"
                    />
                    <PartyFields
                        entries={data.respondents || []}
                        errors={errors.respondents}
                        onUpdate={updateRespondent}
                        onRemove={removeRespondent}
                        onAdd={() =>
                            onUpdate({
                                respondents: [
                                    ...(data.respondents || []),
                                    { name: '', address: '', phone: '', email: '' },
                                ],
                            })
                        }
                        nameLabel="Name of the Other Party"
                        addLabel="Add new respondent"
                    />
                </div>

                <div className="sticky bottom-0 z-10 mt-10 border-t border-[#eee] bg-white/80 px-4 py-4 backdrop-blur-md lg:px-8">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleNext}
                            className="rounded-xl border border-[#151515] bg-[#F1F1F1] px-12 py-3 text-[14px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-10">
            <div className="space-y-5">
                <div>
                    <h2 className="text-xl font-bold text-secondary">Dispute Overview</h2>
                    <p className="mt-1 text-[13px] text-[#555]">
                        Let's understand the type of issue you're dealing with.
                    </p>
                </div>
                <DisputeCards
                    value={data.disputeCategory}
                    onChange={(v) => onUpdate({ disputeCategory: v as 'Civil' | 'Criminal' })}
                />
                <div>
                    <Textarea
                        title="Description"
                        id="dispute-desc"
                        rows={5}
                        placeholder="Write the details of the dispute here"
                        value={data.description}
                        onChange={(e) => {
                            onUpdate({ description: e.target.value });
                            if (errors.description) setErrors({ ...errors, description: '' });
                        }}
                        error={errors.description}
                    />
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <h2 className="text-xl font-bold text-secondary">Statement of Issues & Evidence</h2>
                </div>
                <FileUploadZone files={data.files} setFiles={(fn) => onUpdate({ files: fn(data.files) })} />
            </div>

            {showTerms && (
                <div className="space-y-3 rounded-xl border border-[#eee] bg-white p-5">
                    <label className="flex items-start gap-3 text-[13px] text-secondary">
                        <input
                            type="checkbox"
                            checked={agreedTerms}
                            onChange={(e) => {
                                setAgreedTerms(e.target.checked);
                                if (errors.terms) setErrors({ ...errors, terms: '' });
                            }}
                            className={`mt-0.5 h-4 w-4 accent-primary ${errors.terms ? 'ring-1 ring-red-500' : ''}`}
                        />
                        <span>
                            I have read and agree to the{' '}
                            <span className="font-semibold text-primary">Terms & Conditions</span> and{' '}
                            <span className="font-semibold text-primary">Privacy Policy</span>.
                        </span>
                    </label>
                    {errors.terms && <p className="text-[11px] text-red-500">{errors.terms}</p>}
                    <label className="flex items-start gap-3 text-[13px] text-secondary">
                        <input
                            type="checkbox"
                            checked={agreedAssistance}
                            onChange={(e) => {
                                setAgreedAssistance(e.target.checked);
                                if (errors.assistance) setErrors({ ...errors, assistance: '' });
                            }}
                            className={`mt-0.5 h-4 w-4 accent-primary ${errors.assistance ? 'ring-1 ring-red-500' : ''}`}
                        />
                        I/We, the undersigned, request the assistance of the Multi-Door Court in resolving the matter
                        stated above.
                    </label>
                    {errors.assistance && <p className="text-[11px] text-red-500">{errors.assistance}</p>}
                </div>
            )}

            <div className="sticky bottom-0 z-10 mt-10 border-t border-[#eee] bg-white/80 px-4 py-4 backdrop-blur-md lg:px-8">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => {
                            setStep(1);
                            setErrors({});
                        }}
                        className="rounded-xl border bg-[#F1F1F1] px-10 py-3 text-[14px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                    >
                        Previous Page
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="rounded-xl bg-secondary px-14 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
}
